import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { DataPoint, MetricType } from '../../pages/Dashboard';

interface EditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  item: DataPoint;
  currentValue: number;
  metric: MetricType;
  unit: string;
  onSave: (value: number) => void;
}

export function EditDialog({ isOpen, onClose, item, currentValue, metric, unit, onSave }: EditDialogProps) {
  const [value, setValue] = useState(currentValue);
  const [inputValue, setInputValue] = useState(currentValue.toString());

  useEffect(() => {
    setValue(currentValue);
    setInputValue(currentValue.toString());
  }, [currentValue, isOpen]);

  const handleSave = () => {
    onSave(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    const numValue = parseFloat(newValue);
    if (!isNaN(numValue)) {
      setValue(numValue);
    }
  };

  const handleSliderChange = (values: number[]) => {
    const newValue = values[0];
    setValue(newValue);
    setInputValue(newValue.toString());
  };

  // Calculate slider range based on current value
  const sliderMin = Math.min(-5000, currentValue * 3);
  const sliderMax = Math.max(5000, currentValue * 3);
  const sliderStep = Math.abs(sliderMax - sliderMin) / 1000;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit {item.name}</DialogTitle>
          <DialogDescription>
            Adjust the {metric} value to simulate different environmental impact scenarios.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Description */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 p-3 rounded-xl border border-gray-200/50 shadow-sm">
            <p className="text-sm text-gray-600">{item.description}</p>
          </div>

          {/* Current Value Display */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50/80 p-4 rounded-xl shadow-[0_4px_20px_rgba(0,114,206,0.1)] border border-blue-200/30">
            <p className="text-sm text-gray-600 mb-1">Current Value</p>
            <p className={`text-2xl ${currentValue < 0 ? 'text-[#8CC63F]' : currentValue > 0 ? 'text-[#D71920]' : 'text-gray-900'}`}>
              {currentValue > 0 ? '+' : ''}{currentValue.toLocaleString()} {unit}
            </p>
          </div>

          {/* Input Field */}
          <div className="space-y-2">
            <Label htmlFor="value-input">New Value ({unit})</Label>
            <Input
              id="value-input"
              type="number"
              value={inputValue}
              onChange={handleInputChange}
              step={sliderStep}
              className="text-lg"
            />
            <p className="text-xs text-gray-500">
              {metric === 'TreesSaved'
                ? 'Positive values indicate trees saved (positive environmental impact)'
                : 'Negative values indicate emissions reduction (positive environmental impact)'}
            </p>
          </div>

          {/* Slider */}
          <div className="space-y-2">
            <Label>Adjust with Slider</Label>
            <Slider
              value={[value]}
              onValueChange={handleSliderChange}
              min={sliderMin}
              max={sliderMax}
              step={sliderStep}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>{sliderMin.toFixed(0)}</span>
              <span>{sliderMax.toFixed(0)}</span>
            </div>
          </div>

          {/* Impact Preview */}
          <div className={`p-4 rounded-xl border transition-all duration-300 ${value < 0 ? 'bg-gradient-to-br from-green-50 to-emerald-50/80 border-green-200/50 shadow-[0_4px_20px_rgba(140,198,63,0.1)]' : value > 0 ? 'bg-gradient-to-br from-red-50 to-rose-50/80 border-red-200/50 shadow-[0_4px_20px_rgba(215,25,32,0.1)]' : 'bg-gradient-to-br from-gray-50 to-gray-100/50 border-gray-200/50'}`}>
            <p className="text-sm">
              <span className="text-gray-600">Impact: </span>
              <span className={value < 0 ? 'text-[#8CC63F]' : value > 0 ? 'text-[#D71920]' : 'text-gray-900'}>
                {value > 0 ? '+' : ''}{value.toLocaleString()} {unit}
              </span>
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {value < 0 ? '✓ Positive environmental impact (reduction)' : value > 0 ? '✗ Negative environmental impact (increase)' : 'Neutral impact'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="hover:scale-105 transition-transform duration-200">
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-gradient-to-r from-[#0072CE] to-[#0052a3] hover:shadow-[0_4px_20px_rgba(0,114,206,0.3)] hover:scale-105 transition-all duration-200">
            Apply Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
